/**
 * Module-scoped Okta ID-token store.
 *
 * The token is captured by useAuth after Okta sign-in completes and read
 * by installAuthFetch / manual API helpers so every backend request
 * carries `Authorization: Bearer <id_token>`. Server middleware verifies
 * it against Okta's JWKS — without this, the auth middleware rejects
 * the request with 401.
 *
 * Why a module-scoped ref instead of React state: many call sites use the
 * bare fetch() outside React (e.g. one-shot mutations, retries). A
 * module-scoped ref + getter avoids prop drilling and lets non-React
 * code read the latest token without re-rendering callers.
 */

import { oktaAuth, isDemoMode } from './OktaAuth';

let cachedIdToken: string | null = null;

export const authToken = {
  set(token: string | null) {
    cachedIdToken = token;
  },

  get(): string | null {
    return cachedIdToken;
  },

  current(): string | null {
    if (isDemoMode || !oktaAuth) return null;
    try {
      const fromSdk = oktaAuth.getIdToken();
      if (fromSdk) cachedIdToken = fromSdk;
    } catch {
      // Okta SDK not initialised yet (early app boot); fall through to cache
    }
    return cachedIdToken;
  },

  clear() {
    cachedIdToken = null;
  },
};
